"use client";

import Link from "next/link";
import { AlertCircle } from "lucide-react";
import { cn } from "@qrody/utils";

/**
 * Structural subset of the api-client's `TranslatedError`. The banner
 * never reads the raw ProblemDetail, so `@qrody/ui` stays free of an
 * api-client runtime dependency. The full TranslatedError shape is
 * structurally assignable to BannerError.
 */
export interface BannerError {
  status: number;
  title: string;
  body: string;
  helpLink?: string;
}

/**
 * In-page error banner for failed API calls. Reads a `BannerError`
 * (TranslatedError from @qrody/api-client is structurally assignable) and
 * renders:
 *   - bucketed title (4xx/5xx/offline)
 *   - `user_action` sentence (or a fallback)
 *   - "Mehr erfahren" link to the help-center when a `help_link` is set
 *   - optional retry callback for network / rate-limit situations
 *
 * Kept dependency-free of shadcn's `<Alert>` because we want the frontend
 * bundle small; the styling is Tailwind-only and matches the marketing
 * surface's existing visual grammar.
 */
export function ErrorBanner({
  error,
  onRetry,
  className,
}: {
  error: BannerError;
  onRetry?: () => void;
  className?: string;
}) {
  const tone = bannerTone(error.status);
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        "flex gap-3 rounded-md border p-4 text-sm",
        tone.container,
        className,
      )}
    >
      <AlertCircle className={cn("mt-0.5 size-5 shrink-0", tone.icon)} />
      <div className="flex-1 space-y-2">
        <div className="flex items-baseline justify-between gap-4">
          <p className="font-semibold leading-tight">{error.title}</p>
          <span className="text-xs text-muted-foreground">
            {error.status > 0 ? `HTTP ${error.status}` : "Offline"}
          </span>
        </div>
        <p className="text-muted-foreground">{error.body}</p>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {error.helpLink ? (
            <Link
              href={error.helpLink as never}
              className="inline-flex items-center text-xs font-medium text-primary underline-offset-4 hover:underline"
            >
              Mehr erfahren
            </Link>
          ) : null}
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
            >
              Erneut versuchen
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function bannerTone(status: number) {
  if (status === 0) {
    return {
      container: "border-amber-300 bg-amber-50 text-amber-900",
      icon: "text-amber-600",
    } as const;
  }
  if (status >= 500) {
    return {
      container: "border-red-300 bg-red-50 text-red-900",
      icon: "text-red-600",
    } as const;
  }
  // default 4xx
  return {
    container: "border-orange-300 bg-orange-50 text-orange-900",
    icon: "text-orange-600",
  } as const;
}
